<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Customers</h1>
            <p class="text-muted mb-0">The people and companies you sell to</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add customer
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading</span>
          </div>
          <p class="mt-3 text-muted mb-0">Loading customers…</p>
        </div>

        <!-- Load failed -->
        <div v-else-if="error && !items.length" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="text-danger mb-3" />
          <h2 class="h5">Couldn't load customers</h2>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- First use -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-people" size="2xl" class="text-muted mb-3" />
          <h2 class="h5">No customers yet</h2>
          <p class="text-muted mb-3">
            Add the people and companies you sell to, then pick them on quotations and invoices.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add customer
          </CButton>
        </div>

        <template v-else>
          <CRow class="mb-3">
            <CCol md="6" lg="4">
              <CInput
                v-model="search"
                label="Search"
                placeholder="Name, contact, email or phone"
                class="mb-0"
              >
                <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
              </CInput>
            </CCol>
            <CCol class="d-flex align-items-end justify-content-md-end mt-2 mt-md-0">
              <small class="text-muted">
                {{ filteredItems.length }} of {{ items.length }} customers
              </small>
            </CCol>
          </CRow>

          <!-- Filtered empty -->
          <div v-if="!filteredItems.length" class="text-center py-4">
            <h2 class="h6">No customers match your search</h2>
            <CButton color="light" class="mt-2" @click="search = ''">
              <CIcon name="cil-x" /> Clear search
            </CButton>
          </div>

          <template v-else>
            <!-- Desktop table -->
            <div class="d-none d-lg-block">
              <CDataTable
                :items="filteredItems"
                :fields="fields"
                :items-per-page="25"
                :loading="loading"
                hover
                sorter
                pagination
              >
                <template #name="{ item }">
                  <td class="py-2">
                    <div class="font-weight-bold">{{ item.name || "Unnamed customer" }}</div>
                    <small v-if="item.contactName" class="text-muted">{{ item.contactName }}</small>
                  </td>
                </template>
                <template #email="{ item }">
                  <td class="py-2">
                    <a v-if="item.contactEmail" :href="`mailto:${item.contactEmail}`">{{ item.contactEmail }}</a>
                    <span v-else class="text-muted">—</span>
                  </td>
                </template>
                <template #phone="{ item }">
                  <td class="py-2">
                    <a v-if="phoneOf(item)" :href="`tel:${phoneOf(item)}`">{{ phoneOf(item) }}</a>
                    <span v-else class="text-muted">—</span>
                  </td>
                </template>
                <template #actions="{ item }">
                  <td class="py-2 text-right text-nowrap">
                    <CButton
                      color="light"
                      size="sm"
                      class="mr-1"
                      :aria-label="`Edit ${item.name || 'customer'}`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="light"
                      size="sm"
                      class="text-danger"
                      :aria-label="`Delete ${item.name || 'customer'}`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </div>

            <!-- Mobile card list -->
            <div class="d-lg-none">
              <CCard v-for="item in filteredItems" :key="item.id" class="mb-2">
                <CCardBody class="p-3">
                  <div class="font-weight-bold">{{ item.name || "Unnamed customer" }}</div>
                  <div v-if="item.contactName" class="small text-muted">{{ item.contactName }}</div>
                  <div v-if="item.contactEmail" class="small">
                    <a :href="`mailto:${item.contactEmail}`">{{ item.contactEmail }}</a>
                  </div>
                  <div v-if="phoneOf(item)" class="small">
                    <a :href="`tel:${phoneOf(item)}`">{{ phoneOf(item) }}</a>
                  </div>
                  <div class="d-flex justify-content-between mt-3">
                    <CButton color="light" class="text-danger" @click="showDeleteConfirmation(item)">
                      <CIcon name="cil-trash" /> Delete
                    </CButton>
                    <CButton color="primary" variant="outline" @click="onEdit(item)">
                      <CIcon name="cil-pencil" /> Edit
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </template>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete ${itemToDelete.name || 'this customer'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        {{ itemToDelete.name || "This customer" }} will be removed from your customer list.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <span v-if="deleting" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
          Delete customer
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import CustomerApi from "@/lib/customerApi";

const fields = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email", sorter: false },
  { key: "phone", label: "Phone", sorter: false },
  { key: "actions", label: "", sorter: false, _style: "width:1%" },
];

export default {
  name: "CustomerList",
  data() {
    return {
      loading: true,
      error: false,
      items: [],
      fields,
      search: "",
      api: new CustomerApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  computed: {
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter((c) =>
        [c.name, c.contactName, c.contactEmail, c.contactPhone, c.phone]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Customers"]);
    this.refreshTable();
  },
  methods: {
    phoneOf(item) {
      return item.contactPhone || item.phone || "";
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;
      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result || [];
          self.loading = false;
        })
        .catch((err) => {
          console.error("Load customers failed", err);
          self.loading = false;
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh customers. Check your connection and try again.");
          }
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Customer/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      var self = this;
      const name = self.itemToDelete.name || "the customer";
      self.deleting = true;
      self.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.deleting = false;
          self.warningModal = false;
          self.itemToDelete = {};
          self.$toast.success("Customer deleted");
          self.refreshTable();
        })
        .catch(({ data } = {}) => {
          self.deleting = false;
          self.$toast.error(
            `Couldn't delete ${name}. ` + helper.getErrorMessage(data, "Please try again.")
          );
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/Customer" });
    },
  },
};
</script>
